/**
 * Diamond catalog — display copy for the ledger.
 *
 * This file is client-safe (pure data, no server imports): the app imports
 * it directly to render the ledger's fun name (reason) and category label.
 * Mirrors badges/catalog.ts's BADGE_IDS/BADGES pattern. Deliberately no
 * import from badges/ — the two systems stay independent (see diamonds
 * design doc §9). The minigame identifiers (pocket_pick/cth/daily10) are
 * real shared identity, not display copy, so those come from
 * analytics/minigames.ts's MinigameMode rather than being retyped here.
 */

import type { MinigameMode } from '../analytics/minigames'

export const DIAMOND_REASONS = [
  'pocket_pick_completed',
  'pocket_pick_perfect',
  'cth_completed',
  'cth_perfect',
  'daily10_completed',
  'daily10_perfect',
  'game_finished',
  'lounge_game_finished',
  'play_streak_day',
  'comeback',
  'badge_tier_bronze',
  'badge_tier_silver',
  'badge_tier_gold',
  'badge_tier_platinum',
  'spend'
] as const

export type DiamondReason = (typeof DIAMOND_REASONS)[number]

export type DiamondCategory = MinigameMode | 'game' | 'social' | 'streak' | 'badge' | 'wallet'

export const DIAMOND_REASON_NAMES: Record<DiamondReason, string> = {
  pocket_pick_completed: 'Pocket Pick Played',
  pocket_pick_perfect: 'Perfect Pick',
  cth_completed: 'Hand Complete',
  cth_perfect: 'Perfect Read',
  daily10_completed: 'Daily 10 Complete',
  daily10_perfect: 'Perfect Day',
  game_finished: 'Game Complete',
  lounge_game_finished: 'Lounge Game Complete',
  play_streak_day: 'Streak Day',
  comeback: 'Welcome Back',
  badge_tier_bronze: 'Bronze Tier Earned',
  badge_tier_silver: 'Silver Tier Earned',
  badge_tier_gold: 'Gold Tier Earned',
  badge_tier_platinum: 'Platinum Tier Earned',
  spend: 'Diamonds Spent'
}

export const DIAMOND_CATEGORY_LABELS: Record<DiamondCategory, string> = {
  pocket_pick: 'Pocket Pick',
  cth: 'Complete the Hand',
  daily10: 'Daily 10',
  game: 'Table Play',
  social: 'Social',
  streak: 'Streaks & Comebacks',
  badge: 'Badges',
  wallet: 'Wallet'
}

/** Earn sources only — excludes 'spend', which isn't a fixed payout. Drives the "How to Earn" list. */
export const DIAMOND_EARN_REASONS = DIAMOND_REASONS.filter(
  (reason): reason is Exclude<DiamondReason, 'spend'> => reason !== 'spend'
)

/**
 * Imperative "how to earn" copy — distinct from DIAMOND_REASON_NAMES (the
 * ledger's past-tense event names). Adapted from badges/catalog.ts's
 * descriptionSingular fields (not imported — see the independence note
 * above), dropping "first" since these are repeatable, not one-time.
 */
export const DIAMOND_EARN_PROMPTS: Record<Exclude<DiamondReason, 'spend'>, string> = {
  pocket_pick_completed: 'Play Pocket Pick',
  pocket_pick_perfect: 'Pick the best card every round in Pocket Pick',
  cth_completed: 'Play Complete the Hand',
  cth_perfect: 'Get every round right in Complete the Hand',
  daily10_completed: 'Play Daily 10',
  daily10_perfect: 'Finish Daily 10 with maximum possible chips',
  game_finished: 'Play a game',
  lounge_game_finished: 'Play a lounge game',
  play_streak_day: 'Play on back-to-back days',
  comeback: 'Come back after a week away',
  badge_tier_bronze: 'Reach Bronze on any badge',
  badge_tier_silver: 'Reach Silver on any badge',
  badge_tier_gold: 'Reach Gold on any badge',
  badge_tier_platinum: 'Reach Platinum on any badge'
}

/**
 * The single source of truth for each reason's payout. diamonds/helpers/transaction.ts
 * (server-only) reads from this — never hardcode a payout amount there.
 */
export const DIAMOND_REASON_PAYOUTS: Record<Exclude<DiamondReason, 'spend'>, number> = {
  pocket_pick_completed: 5,
  pocket_pick_perfect: 5,
  cth_completed: 5,
  cth_perfect: 5,
  daily10_completed: 5,
  daily10_perfect: 5,
  game_finished: 2,
  lounge_game_finished: 2,
  play_streak_day: 2,
  comeback: 25,
  badge_tier_bronze: 10,
  badge_tier_silver: 25,
  badge_tier_gold: 50,
  badge_tier_platinum: 100
}

/**
 * The single source of truth for each earn reason's category. diamonds/helpers/transaction.ts
 * (server-only) reads from this — never hardcode a category there.
 */
export const DIAMOND_REASON_CATEGORIES: Record<Exclude<DiamondReason, 'spend'>, DiamondCategory> = {
  pocket_pick_completed: 'pocket_pick',
  pocket_pick_perfect: 'pocket_pick',
  cth_completed: 'cth',
  cth_perfect: 'cth',
  daily10_completed: 'daily10',
  daily10_perfect: 'daily10',
  game_finished: 'game',
  lounge_game_finished: 'game', // filed with the game's own row (game / gameId); How to Earn lists it under Social
  play_streak_day: 'streak',
  comeback: 'streak',
  badge_tier_bronze: 'badge',
  badge_tier_silver: 'badge',
  badge_tier_gold: 'badge',
  badge_tier_platinum: 'badge'
}

/** Days since the last play that count as a comeback (Oct 1 → Oct 8 qualifies). */
export const COMEBACK_MIN_DAYS_AWAY = 7
